/* TERNA — profitto, drawdown, operazioni.
 *
 * Regola 1 di STATO.md: il profitto non va mai da solo. Se di una riga si
 * mostrano tre valori, sono questi tre, su ogni larghezza. Non esiste una
 * variante "solo profitto": non è un'omissione, è un'illusione.
 *
 * Regola 2: ogni numero col suo periodo di misura, perché le stesse strategie
 * danno esiti opposti su 2017–2026 e su 2022–2026. Il periodo è obbligatorio
 * nella firma proprio per rendere impossibile dimenticarlo.
 */

import { DistintivoPeriodo, Etichetta } from './Base';
import { ScheletroLista, StatoErrore, StatoVuoto } from './Stati';
import { drawdown as fmtDrawdown, num, pct } from '../lib/formato';
import { tonoRendimento } from '../lib/metriche';
import type { StatoDati } from '../api/usaRichiesta';

export type DensitaTerna = 'piena' | 'compatta' | 'inline' | 'riquadri';

export interface DatiTerna {
  /** Profitto della strategia in percentuale. */
  profitto: number | null;
  /** Drawdown massimo. Il segno non conta: si mostra sempre negativo. */
  drawdown: number | null;
  operazioni: number | null;
}

export interface PropsTerna extends DatiTerna {
  densita?: DensitaTerna;
  /** Etichetta del periodo, es. "2017–2026". Obbligatoria. */
  periodo: string;
  /** Durata leggibile, es. "9a 4m". Accanto al periodo, dove il periodo si vede. */
  durata?: string;
  stato?: StatoDati;
  onRiprova?: () => void;
}

function testoProfitto(v: number | null): string {
  return v == null ? 'n/d' : pct(v);
}

function testoDrawdown(v: number | null): string {
  return v == null ? 'n/d' : fmtDrawdown(v);
}

function testoOperazioni(v: number | null): string {
  return v == null ? 'n/d' : num(v);
}

export function TernaMetriche({
  profitto,
  drawdown,
  operazioni,
  periodo,
  durata,
  densita = 'compatta',
  stato = 'pieno',
  onRiprova,
}: PropsTerna) {
  if (stato === 'caricamento') return <ScheletroLista righe={1} conIcona={false} />;
  if (stato === 'errore')
    return <StatoErrore titolo="Metriche non disponibili" onRiprova={onRiprova} />;
  if (stato === 'vuoto')
    return <StatoVuoto titolo="Nessuna misura" causa="Il backtest non ha prodotto operazioni nel periodo." />;

  const classeProfitto = `sb-tono-${tonoRendimento(profitto)}`;

  if (densita === 'inline') {
    return (
      <span className="sb-terna sb-terna--inline">
        <span className={`sb-terna__valore ${classeProfitto}`}>{testoProfitto(profitto)}</span>
        <span className="sb-terna__barra">/</span>
        <span className="sb-terna__valore sb-tono-perdita">{testoDrawdown(drawdown)}</span>
        <span className="sb-terna__barra">/</span>
        <span className="sb-terna__ops">{testoOperazioni(operazioni)} ops</span>
        <DistintivoPeriodo>{periodo}</DistintivoPeriodo>
      </span>
    );
  }

  if (densita === 'riquadri') {
    return (
      <div className="sb-terna">
        <div className="sb-terna--riquadri">
          <div className="sb-terna__riquadro">
            <div className="sb-terna__voce-etichetta">PROFITTO</div>
            <div className={`sb-terna__voce-valore ${classeProfitto}`}>{testoProfitto(profitto)}</div>
          </div>
          <div className="sb-terna__riquadro">
            <div className="sb-terna__voce-etichetta">DRAWDOWN</div>
            <div className="sb-terna__voce-valore sb-tono-perdita">{testoDrawdown(drawdown)}</div>
          </div>
          <div className="sb-terna__riquadro">
            <div className="sb-terna__voce-etichetta">OPS</div>
            <div className="sb-terna__voce-valore sb-tono-neutro">{testoOperazioni(operazioni)}</div>
          </div>
        </div>
        {/* Il periodo era obbligatorio nella firma e assente dallo schermo:
            un obbligo che non diventa un obbligo di schermo non protegge
            nessuno, e questa è la densità delle schede su telefono, cioè
            proprio il caso in cui il periodo serve di più. Ogni chiamante
            metteva un distintivo per conto suo, e il primo che se lo fosse
            dimenticato avrebbe mostrato tre numeri senza il loro periodo. */}
        <div className="sb-terna__periodo">
          {periodo}
          {durata ? ` · ${durata}` : ''}
        </div>
      </div>
    );
  }

  if (densita === 'compatta') {
    return (
      <div className="sb-terna">
        <div className="sb-terna--compatta">
          <div>
            <div className="sb-terna__voce-etichetta">PROFITTO</div>
            <div className={`sb-terna__voce-valore ${classeProfitto}`}>{testoProfitto(profitto)}</div>
          </div>
          <span className="sb-divisore-verticale" aria-hidden="true" />
          <div>
            <div className="sb-terna__voce-etichetta">DD</div>
            <div className="sb-terna__voce-valore sb-tono-perdita">{testoDrawdown(drawdown)}</div>
          </div>
          <span className="sb-divisore-verticale" aria-hidden="true" />
          <div>
            <div className="sb-terna__voce-etichetta">OPS</div>
            <div className="sb-terna__voce-valore sb-tono-neutro">{testoOperazioni(operazioni)}</div>
          </div>
        </div>
        <div className="sb-terna__periodo">
          {periodo}
          {durata ? ` · ${durata}` : ''}
        </div>
      </div>
    );
  }

  return (
    <div className="sb-terna sb-terna--piena">
      <div className="sb-terna__testata">
        <span className={`sb-numero sb-terna__principale ${classeProfitto}`}>
          {testoProfitto(profitto)}
        </span>
        <DistintivoPeriodo>{periodo}</DistintivoPeriodo>
      </div>
      <div className="sb-terna__secondari">
        <div>
          <Etichetta>Drawdown</Etichetta>
          <div className="sb-terna__voce-valore sb-tono-perdita">{testoDrawdown(drawdown)}</div>
        </div>
        <div>
          <Etichetta>Operazioni</Etichetta>
          <div className="sb-terna__voce-valore sb-tono-neutro">{testoOperazioni(operazioni)}</div>
        </div>
      </div>
    </div>
  );
}
